import { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';

const springTransition = {
  type: 'spring',
  stiffness: 80,
  damping: 20,
};

interface Placement {
  id: number;
  engineer: string;
  role: string;
  company: string;
  tech: string;
  time: string;
  type: 'placement' | 'available' | 'interview';
}

const recentActivity: Placement[] = [
  { id: 1, engineer: 'Senior React Dev', role: 'Frontend Lead', company: 'FinCorp', tech: 'React', time: '2 min ago', type: 'placement' },
  { id: 2, engineer: 'Node.js Engineer', role: 'Backend Dev', company: 'HealthBridge', tech: 'Node.js', time: '5 min ago', type: 'available' },
  { id: 3, engineer: 'Python Data Engineer', role: 'ML Engineer', company: 'DataViz', tech: 'Python', time: '8 min ago', type: 'interview' },
  { id: 4, engineer: 'Full-Stack Engineer', role: 'Tech Lead', company: 'CloudScale', tech: 'Next.js', time: '12 min ago', type: 'placement' },
  { id: 5, engineer: 'DevOps Specialist', role: 'SRE', company: 'LogiFlow', tech: 'Kubernetes', time: '15 min ago', type: 'available' },
  { id: 6, engineer: 'Mobile Developer', role: 'iOS Lead', company: 'AppVenture', tech: 'Swift', time: '18 min ago', type: 'interview' },
  { id: 7, engineer: 'Java Architect', role: 'Backend Lead', company: 'BankSecure', tech: 'Java', time: '22 min ago', type: 'placement' },
  { id: 8, engineer: 'Vue.js Developer', role: 'Frontend Dev', company: 'ShopDirect', tech: 'Vue.js', time: '25 min ago', type: 'available' },
];

const techAvailability = [
  { tech: 'React', count: 47, color: 'bg-primary-500' },
  { tech: 'Node.js', count: 38, color: 'bg-primary-500' },
  { tech: 'Python', count: 35, color: 'bg-primary-500' },
  { tech: 'Java', count: 29, color: 'bg-primary-500' },
  { tech: '.NET', count: 22, color: 'bg-primary-500' },
  { tech: 'DevOps', count: 19, color: 'bg-accent-500' },
  { tech: 'Mobile', count: 16, color: 'bg-accent-500' },
  { tech: 'AI/ML', count: 14, color: 'bg-accent-500' },
];

const metrics = [
  { label: 'Engineers Available Now', value: 482, suffix: '+', icon: 'ri-user-search-line', change: '+12', changeLabel: 'this week' },
  { label: 'Avg. Response Time', value: 2, suffix: 'h', icon: 'ri-flashlight-line', change: '-30 min', changeLabel: 'vs last month' },
  { label: 'Recent Placements', value: 34, suffix: '', icon: 'ri-briefcase-line', change: '+8', changeLabel: 'this week' },
  { label: 'Active Interviews', value: 17, suffix: '', icon: 'ri-video-chat-line', change: '+5', changeLabel: 'today' },
];

function AnimatedCounter({ target, suffix, inView }: { target: number; suffix: string; inView: boolean }) {
  const [count, setCount] = useState(0);
  const countRef = useRef(0);
  const rafRef = useRef<number>(0);

  useEffect(() => {
    if (!inView) return;
    countRef.current = 0;
    const duration = 2000;
    const startTime = performance.now();

    const animate = (now: number) => {
      const elapsed = now - startTime;
      const progress = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      countRef.current = Math.round(eased * target);
      setCount(countRef.current);
      if (progress < 1) {
        rafRef.current = requestAnimationFrame(animate);
      }
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rafRef.current);
  }, [inView, target]);

  return (
    <span>{count}{suffix}</span>
  );
}

export default function LiveTalentDashboard() {
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, amount: 0.1 });
  const [activeFilter, setActiveFilter] = useState('all');
  const [tickerIndex, setTickerIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setTickerIndex((prev) => (prev + 1) % recentActivity.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  const filteredActivity = activeFilter === 'all'
    ? recentActivity
    : recentActivity.filter(a => a.type === activeFilter);

  const typeConfig = {
    placement: { label: 'Placed', icon: 'ri-check-double-line', color: 'text-emerald-600 bg-emerald-50 border-emerald-200' },
    available: { label: 'Available', icon: 'ri-user-add-line', color: 'text-primary-600 bg-primary-50 border-primary-200' },
    interview: { label: 'Interview', icon: 'ri-video-chat-line', color: 'text-amber-600 bg-amber-50 border-amber-200' },
  };

  return (
    <section id="live-dashboard" className="py-16 md:py-24 bg-white relative overflow-hidden" ref={sectionRef}>
      {/* Subtle background pattern */}
      <div className="absolute inset-0 opacity-[0.03]" style={{
        backgroundImage: 'radial-gradient(circle at 1px 1px, #95c302 1px, transparent 0)',
        backgroundSize: '32px 32px',
      }}></div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 40, scale: 0.95 }}
          animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
          transition={{ ...springTransition, duration: 0.7 }}
          className="text-center mb-12 md:mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ delay: 0.1, ...springTransition }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 border border-primary-200 rounded-full mb-4 md:mb-6 shadow-sm"
          >
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500"></span>
            </span>
            <span className="text-sm font-medium text-gray-700">Live Talent Network</span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.15, ...springTransition }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-display font-bold text-navy-900 leading-tight mb-4 md:mb-6"
          >
            Real-Time
            <br />
            <span className="text-primary-600">Talent Dashboard</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="text-base md:text-xl text-gray-600 max-w-3xl mx-auto px-2"
          >
            See exactly how our talent pipeline moves in real time. Engineers are matched, interviewed, and placed around the clock.
          </motion.p>
        </motion.div>

        {/* Main Dashboard Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 md:gap-8">
          {/* Left: Big Metrics */}
          <div className="lg:col-span-2 space-y-6 md:space-y-8">
            {/* Metric Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
              {metrics.map((metric, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 40, scale: 0.9 }}
                  animate={isInView ? { opacity: 1, y: 0, scale: 1 } : {}}
                  transition={{ ...springTransition, duration: 0.7, delay: index * 0.1 }}
                  className="bg-white rounded-xl border border-gray-100 p-4 md:p-5 hover:border-primary-200 hover:shadow-md transition-all duration-300 group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-9 h-9 md:w-10 md:h-10 flex items-center justify-center rounded-lg bg-primary-50 group-hover:bg-primary-100 transition-colors">
                      <i className={`${metric.icon} text-lg text-primary-600`}></i>
                    </div>
                    <span className={`text-xs font-semibold ${metric.change.startsWith('+') ? 'text-emerald-600' : 'text-primary-600'} bg-gray-50 px-2 py-0.5 rounded-full`}>
                      {metric.change}
                    </span>
                  </div>
                  <div className="text-2xl md:text-3xl font-bold text-navy-900 mb-1">
                    <AnimatedCounter target={metric.value} suffix={metric.suffix} inView={isInView} />
                  </div>
                  <p className="text-xs md:text-sm text-gray-500">{metric.label}</p>
                  <p className="text-[10px] md:text-xs text-gray-400 mt-1">{metric.changeLabel}</p>
                </motion.div>
              ))}
            </div>

            {/* Availability by Tech */}
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ ...springTransition, duration: 0.7, delay: 0.4 }}
              className="bg-white rounded-xl border border-gray-100 p-5 md:p-6"
            >
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-base md:text-lg font-bold text-navy-900">Availability by Tech Stack</h3>
                  <p className="text-xs md:text-sm text-gray-500 mt-0.5">Engineers ready to start this week</p>
                </div>
                <div className="hidden sm:flex items-center gap-2">
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="w-2 h-2 rounded-full bg-primary-500"></span>
                    Available
                  </span>
                  <span className="flex items-center gap-1 text-xs text-gray-500">
                    <span className="w-2 h-2 rounded-full bg-accent-500"></span>
                    High Demand
                  </span>
                </div>
              </div>

              <div className="space-y-3 md:space-y-4">
                {techAvailability.map((item, index) => (
                  <motion.div
                    key={item.tech}
                    initial={{ opacity: 0, x: -20 }}
                    animate={isInView ? { opacity: 1, x: 0 } : {}}
                    transition={{ delay: 0.5 + index * 0.08, duration: 0.5 }}
                    className="group"
                  >
                    <div className="flex items-center justify-between mb-1.5">
                      <div className="flex items-center gap-2 md:gap-3">
                        <span className="text-sm font-semibold text-navy-900 w-16 md:w-20">{item.tech}</span>
                        <span className="text-xs text-gray-500 group-hover:text-primary-600 transition-colors">{item.count} engineers</span>
                      </div>
                      <span className="text-xs font-medium text-gray-400">{item.count} available</span>
                    </div>
                    <div className="relative h-2.5 md:h-3 bg-gray-100 rounded-full overflow-hidden">
                      <motion.div
                        initial={{ width: 0 }}
                        animate={isInView ? { width: `${(item.count / 50) * 100}%` } : {}}
                        transition={{ delay: 0.6 + index * 0.08, duration: 1, ease: 'easeOut' }}
                        className={`h-full rounded-full ${item.color} relative`}
                      >
                        <div className="absolute inset-0 bg-white/20 animate-pulse rounded-full"></div>
                      </motion.div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right: Live Activity Feed */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ ...springTransition, duration: 0.7, delay: 0.3 }}
            className="bg-white rounded-xl border border-gray-100 p-5 md:p-6 flex flex-col"
          >
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-base md:text-lg font-bold text-navy-900 flex items-center gap-2">
                  Live Activity
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                  </span>
                </h3>
                <p className="text-xs md:text-sm text-gray-500 mt-0.5">Recent placements and updates</p>
              </div>
            </div>

            {/* Filter Tabs */}
            <div className="flex gap-2 mb-4 overflow-x-auto pb-1 scrollbar-hide">
              {[
                { key: 'all', label: 'All' },
                { key: 'placement', label: 'Placements' },
                { key: 'available', label: 'Available' },
                { key: 'interview', label: 'Interviews' },
              ].map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveFilter(tab.key)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-full transition-all whitespace-nowrap cursor-pointer ${
                    activeFilter === tab.key
                      ? 'bg-primary-500 text-white shadow-sm'
                      : 'bg-gray-50 text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Activity List */}
            <div className="flex-1 space-y-3 min-h-[320px]">
              {filteredActivity.slice(0, 6).map((item, index) => {
                const config = typeConfig[item.type];
                const isHighlighted = tickerIndex === index;

                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.06 }}
                    className={`flex items-start gap-3 p-3 rounded-lg transition-all duration-300 ${
                      isHighlighted
                        ? 'bg-primary-50 border border-primary-100 shadow-sm'
                        : 'bg-gray-50/50 hover:bg-gray-50'
                    }`}
                  >
                    <div className={`w-8 h-8 flex items-center justify-center rounded-lg border flex-shrink-0 ${config.color}`}>
                      <i className={`${config.icon} text-sm`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between gap-2">
                        <p className="text-sm font-semibold text-navy-900 truncate">{item.engineer}</p>
                        <span className="text-[10px] md:text-xs text-gray-400 whitespace-nowrap flex-shrink-0">{item.time}</span>
                      </div>
                      <p className="text-xs text-gray-600 mt-0.5">
                        {item.role} · {item.company}
                      </p>
                      <div className="flex items-center gap-2 mt-1.5">
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-600">
                          {item.tech}
                        </span>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-[10px] font-medium ${config.color}`}>
                          {config.label}
                        </span>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>

            {/* Ticker Banner */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <i className="ri-bar-chart-box-line text-primary-600"></i>
                <span className="font-medium">Now:</span>
                <motion.span
                  key={tickerIndex}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  className="text-navy-900 font-medium"
                >
                  {recentActivity[tickerIndex].engineer} {recentActivity[tickerIndex].type === 'placement' ? 'placed at' : recentActivity[tickerIndex].type === 'interview' ? 'interviewing with' : 'now available —'}{` `}
                  {recentActivity[tickerIndex].type === 'available' ? 'contact us to match' : recentActivity[tickerIndex].company}
                </motion.span>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom CTA Row */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ delay: 0.8, ...springTransition }}
          className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          <div className="bg-primary-50 rounded-xl p-4 md:p-5 border border-primary-100 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-500 flex-shrink-0">
              <i className="ri-user-add-line text-lg text-white"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-navy-900">New engineers weekly</p>
              <p className="text-xs text-gray-600">15–20 pre-vetted candidates join every week</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-5 border border-gray-100 flex items-center gap-3">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-emerald-500 flex-shrink-0">
              <i className="ri-shield-check-line text-lg text-white"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-navy-900">4-stage vetting</p>
              <p className="text-xs text-gray-600">Only 12% of applicants pass our full process</p>
            </div>
          </div>

          <div className="bg-white rounded-xl p-4 md:p-5 border border-gray-100 flex items-center gap-3 hover:border-primary-200 transition-colors cursor-pointer group"
            onClick={() => {
              const el = document.getElementById('cta');
              if (el) {
                const offset = 80;
                const pos = el.getBoundingClientRect().top + window.pageYOffset - offset;
                window.scrollTo({ top: pos, behavior: 'smooth' });
              }
            }}
          >
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 group-hover:bg-primary-100 transition-colors flex-shrink-0">
              <i className="ri-arrow-right-line text-lg text-primary-600 group-hover:translate-x-0.5 transition-transform"></i>
            </div>
            <div>
              <p className="text-sm font-bold text-navy-900 group-hover:text-primary-600 transition-colors">Request a talent match</p>
              <p className="text-xs text-gray-600">Get candidates within 48 hours</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}